/**
 * Living scene over a photo: incense smoke and candle light drawn on a canvas.
 * Used on the Book of Changes table and the fortune ball.
 *
 * Points are given in photo pixels and mapped to the scene the same way CSS paints the background:
 * `<posX> <posY> / cover`. If a photo changes, update its config (size and the two points).
 */
export const ICHING_TABLE = {
  photo: { w: 1672, h: 941, posX: 0.5, posY: 0.3 }, // src/img/iching/table.webp
  incense: { x: 300, y: 118 }, // top of the burner lid
  flame: { x: 1400, y: 150, w: 26, h: 104 }, // centre and size of the candle flame
};

// runes: the closed pouch, then the same table with the pouch opened and stones spilled
export const RUNES_CLOSED = {
  photo: { w: 1536, h: 1024, posX: 0.58, posY: 0.5 }, // src/img/runes/pouch.webp
  incense: { x: 575, y: 172 },
  flame: { x: 278, y: 76, w: 30, h: 112 },
};

export const RUNES_SPILLED = {
  photo: { w: 1536, h: 1024, posX: 0.58, posY: 0.5 }, // src/img/runes/spilled.webp
  incense: { x: 545, y: 84 },
  flame: { x: 240, y: 64, w: 30, h: 112 },
};

export const BALL_TABLE = {
  photo: { w: 1536, h: 1024, posX: 0.5, posY: 0.5 }, // src/img/ball/ball.webp
  incense: { x: 1370, y: 292 },
  flame: { x: 211, y: 178, w: 28, h: 104 },
};

// where a photo point lands inside a box of w × h painted with background-size: cover
export function coverMap(photo, w, h) {
  const scale = Math.max(w / photo.w, h / photo.h);
  const ox = (w - photo.w * scale) * photo.posX;
  const oy = (h - photo.h * scale) * photo.posY;
  return { scale, ox, oy, map: p => ({ x: ox + p.x * scale, y: oy + p.y * scale }) };
}

/**
 * Returns { set(config) } to switch to another photo of the same scene (runes: closed → spilled pouch).
 * With reduced motion nothing is drawn and the controller does nothing.
 */
export function initTableFx(scene, config = ICHING_TABLE) {
  if (!scene || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return { set() {} };
  let { photo: PHOTO, incense: INCENSE, flame: FLAME } = config;

  const canvas = document.createElement('canvas');
  canvas.className = 'scene__fx';
  canvas.setAttribute('aria-hidden', 'true');
  scene.prepend(canvas);
  const ctx = canvas.getContext('2d');

  let W = 0;
  let H = 0;
  let scale = 1;
  let ox = 0;
  let oy = 0;
  let dpr = 1;

  function resize() {
    W = scene.clientWidth;
    H = scene.clientHeight;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    scale = Math.max(W / PHOTO.w, H / PHOTO.h);
    ox = (W - PHOTO.w * scale) * PHOTO.posX;
    oy = (H - PHOTO.h * scale) * PHOTO.posY;
  }
  const map = p => ({ x: ox + p.x * scale, y: oy + p.y * scale });

  // one soft blob, reused for every smoke particle
  const blob = document.createElement('canvas');
  blob.width = blob.height = 64;
  const b = blob.getContext('2d');
  const g = b.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(225,215,200,0.55)');
  g.addColorStop(0.45, 'rgba(210,200,185,0.22)');
  g.addColorStop(1, 'rgba(200,190,175,0)');
  b.fillStyle = g;
  b.fillRect(0, 0, 64, 64);

  const smoke = [];
  let spawn = 0;

  function emit() {
    smoke.push({
      age: 0,
      life: 4.5 + Math.random() * 2.5,
      x: (Math.random() - 0.5) * 4,
      y: 0,
      vy: 26 + Math.random() * 10, // photo px per second, upwards
      seed: Math.random() * 1000,
      size: 3 + Math.random() * 3,
    });
  }

  // smooth pseudo-noise: a few sines, enough for a lazy ribbon of smoke
  const wave = (t, s) => Math.sin(t * 0.9 + s) * 0.6 + Math.sin(t * 2.3 + s * 1.7) * 0.3 + Math.sin(t * 0.37 + s * 0.5);

  function drawSmoke(dt, time, paint = true) {
    spawn += dt * 14;
    while (spawn > 1) {
      emit();
      spawn -= 1;
    }
    const src = map(INCENSE);
    ctx.globalCompositeOperation = 'screen';
    for (let i = smoke.length - 1; i >= 0; i--) {
      const p = smoke[i];
      p.age += dt;
      if (p.age > p.life) {
        smoke.splice(i, 1);
        continue;
      }
      const k = p.age / p.life;
      p.y -= p.vy * dt;
      const rise = -p.y;
      // drifts up and slightly right (as in the photo), swaying more the higher it gets
      const sway = wave(time * 0.6 - rise * 0.025, p.seed * 0.004) * (4 + rise * 0.3) + rise * 0.35;
      const x = src.x + (p.x + sway) * scale;
      const y = src.y + p.y * scale;
      const r = (p.size + rise * 0.16) * scale;
      if (!paint) continue;
      const a = Math.min(1, p.age * 2.5) * (1 - k) * (1 - k) * 0.26;
      ctx.globalAlpha = a;
      ctx.drawImage(blob, x - r, y - r, r * 2, r * 2);
    }
    ctx.globalAlpha = 1;
  }

  // candle: breathing halo + a flickering bright core over the photo's flame
  let flick = 1;
  let flickTarget = 1;
  let swayX = 0;
  let swayTarget = 0;

  function drawCandle(dt) {
    if (Math.random() < dt * 9) {
      flickTarget = 0.75 + Math.random() * 0.4;
      swayTarget = (Math.random() - 0.5) * 0.5;
    }
    flick += (flickTarget - flick) * Math.min(1, dt * 12);
    swayX += (swayTarget - swayX) * Math.min(1, dt * 6);

    const f = map(FLAME);
    const fw = FLAME.w * scale;
    const fh = FLAME.h * scale;
    ctx.globalCompositeOperation = 'lighter';

    // halo
    const R = fh * 5.5 * (0.92 + flick * 0.08);
    const halo = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, R);
    halo.addColorStop(0, `rgba(255,170,70,${0.22 * flick})`);
    halo.addColorStop(0.35, `rgba(255,130,40,${0.08 * flick})`);
    halo.addColorStop(1, 'rgba(255,120,30,0)');
    ctx.fillStyle = halo;
    ctx.fillRect(f.x - R, f.y - R, R * 2, R * 2);

    // core: an ellipse stretched upwards, leaning with the sway
    ctx.save();
    ctx.translate(f.x, f.y + fh * 0.35);
    ctx.rotate(swayX * 0.18);
    ctx.scale(1, 0.9 + flick * 0.15);
    const core = ctx.createRadialGradient(0, -fh * 0.25, 0, 0, -fh * 0.25, fh * 0.55);
    core.addColorStop(0, `rgba(255,240,200,${0.35 * flick})`);
    core.addColorStop(0.5, `rgba(255,180,80,${0.15 * flick})`);
    core.addColorStop(1, 'rgba(255,150,50,0)');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.ellipse(0, -fh * 0.3, fw * 0.9, fh * 0.65, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  let last = 0;
  let raf = 0;
  let visible = false;

  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000 || 0.016);
    last = now;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    drawSmoke(dt, now / 1000);
    drawCandle(dt);
    raf = requestAnimationFrame(frame);
  }

  function run() {
    cancelAnimationFrame(raf);
    if (visible && !document.hidden) {
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
  }

  resize();
  // pre-fill so the smoke is already rising when the page opens
  for (let i = 0; i < 180; i++) drawSmoke(1 / 30, i / 30, false);

  new ResizeObserver(resize).observe(scene);
  new IntersectionObserver(([e]) => {
    visible = e.isIntersecting;
    run();
  }).observe(scene);
  document.addEventListener('visibilitychange', run);

  return {
    set(next) {
      ({ photo: PHOTO, incense: INCENSE, flame: FLAME } = next);
      smoke.length = 0;
      resize();
      for (let i = 0; i < 120; i++) drawSmoke(1 / 30, i / 30, false);
    },
  };
}
